"use client";

/**
 * @file ソート可能なサイドバーグループコンポーネント
 * サイドバー内でビューをドラッグ&ドロップで並び替える機能を提供。
 * 「すべて」や「設定」などのソート不可アイテムは固定位置に維持。
 */

import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useLingui } from "@lingui/react/macro";
import { usePathname } from "next/navigation";
import React from "react";
import { toast } from "sonner";
import {
  SortableProvider,
  useSortable,
} from "@/app/[lang]/[workspaceId]/_components/sortable/sortable-context";
import { SidebarMenuSub } from "@/components/ui/sidebar";
import { SortableItem as SortableItemComponent } from "@/components/ui/sortable-item";
import { logger } from "@/lib/logger";
import { DragOverlayPortal } from "../layout/sidebar/drag-overlay-portal";
import { useSidebarSearchProjectMatch } from "../layout/sidebar/sidebar-search-context";
import type { ProjectGroupType } from "../layout/sidebar/types";
import { useDragAndDrop, usePreventClickDuringDrag } from "./sortable-hooks";
import { SidebarSortableItem } from "./sortable-sidebar-item";
import SortableOverlay from "./sortable-sidebar-overlay";
import {
  type AnySidebarItem,
  type UpdateViewOrderFunction,
} from "./sortable-types";
import { categorizeItems, extractSpecialItems } from "./sortable-utils";

/** コンポーネントのProps型 */
interface SortableSidebarGroupProps<T extends AnySidebarItem = AnySidebarItem> {
  /** 表示・ソート対象のビューアイテムリスト */
  views: T[];
  /** ビュー順序更新用のサーバーアクション */
  updateViewOrder: UpdateViewOrderFunction;
  /** プロジェクトID（表示用） */
  projectId: string;
  /** 検索 tree 上のグループID */
  projectType: Exclude<ProjectGroupType, "dashboards">;
  /** サーバーアクション用ID（searchAnalyticsIDなど） */
  actionId: string;
  /** スタイリング用CSSクラス名 */
  className?: string;
  /** ビューアイテムのhref生成関数 */
  generateViewHref: (item: T) => string;
  /** ビューコンテンツのレンダリング関数 */
  renderViewContent: (item: T) => React.ReactNode;
  /** 並び替え機能を有効にするかどうか */
  isReorderEnabled?: boolean;
}

/**
 * ソート可能なサイドバーグループコンポーネント
 * ソート不可アイテムを固定位置に維持しながら、ビューの並び替えを可能にする
 */
export default function SortableSidebarGroup<
  T extends AnySidebarItem = AnySidebarItem,
>({
  views,
  updateViewOrder,
  projectId,
  projectType,
  actionId,
  className,
  generateViewHref,
  renderViewContent,
  isReorderEnabled = true,
}: SortableSidebarGroupProps<T>) {
  const { t } = useLingui();

  /**
   * ドラッグ&ドロップ後の新しい順序を保存
   * ソート不可アイテムを除外してサーバーを更新
   */
  const handleSaveOrder = async (items: AnySidebarItem[]) => {
    try {
      // ソート不可アイテムを除外して順序付けされたIDを取得
      const sortableItems = items.filter((item) => item.sortable !== false);
      const orderedIds = sortableItems.map((item) => item.id);

      // 最初のビューのタイプに基づいてプロジェクトIDを取得
      const firstView = sortableItems[0];
      if (!firstView) {
        return; // 更新するソート可能なビューがない
      }

      // 適切なパラメータでサーバーアクションを呼び出す
      const result = await updateViewOrder(actionId, orderedIds, projectId);

      if (result.error) {
        toast.error(result.error);
      }
    } catch (error) {
      logger.error("Failed to save view order:", error);
      toast.error(t`Failed to update view order. Please try again.`);
    }
  };

  return (
    <SortableProvider initialItems={views} onSaveOrder={handleSaveOrder}>
      <SidebarViewsList
        className={className}
        generateViewHref={generateViewHref}
        renderViewContent={renderViewContent}
        isReorderEnabled={isReorderEnabled}
        projectId={projectId}
        projectType={projectType}
      />
    </SortableProvider>
  );
}

/**
 * ソート可能なビューリストをレンダリングする内部コンポーネント
 * sortable contextにアクセスするために分離
 */
function SidebarViewsList<T extends AnySidebarItem = AnySidebarItem>({
  className,
  generateViewHref,
  renderViewContent,
  isReorderEnabled,
  projectId,
  projectType,
}: {
  className?: string;
  generateViewHref: (item: T) => string;
  renderViewContent: (item: T) => React.ReactNode;
  isReorderEnabled: boolean;
  projectId: string;
  projectType: Exclude<ProjectGroupType, "dashboards">;
}) {
  const { items, setItems, saveOrder } = useSortable<T>();
  const pathname = usePathname();
  const { isFiltering, match } = useSidebarSearchProjectMatch(
    projectType,
    projectId,
  );
  const visibleItems = React.useMemo(() => {
    if (!isFiltering || match?.includeAllItems) {
      return items;
    }
    const visibleItemIds = new Set(match?.itemIds ?? []);
    return items.filter((item) => visibleItemIds.has(item.id));
  }, [isFiltering, items, match]);

  // ドラッグ&ドロップのロジックをカスタムフックで管理
  const {
    activeId,
    isDragging,
    dragEndTimeRef,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useDragAndDrop({
    items: visibleItems,
    setItems,
    saveOrder,
    isEnabled: isReorderEnabled && !isFiltering,
  });

  // クリック防止のロジックをカスタムフックで管理
  const containerRef = usePreventClickDuringDrag({
    isDragging,
    dragEndTimeRef,
  });

  // バランスの取れたアクティベーション制約でドラッグセンサーを設定
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // 10px移動してからドラッグ開始
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: (_, { context: { active, droppableRects } }) => {
        // 垂直リスト用のカスタムキーボード座標
        return {
          x: 0,
          y: active ? (droppableRects.get(active.id)?.top ?? 0) : 0,
        };
      },
    }),
  );

  /**
   * ビューアイテムのレンダリング（メモ化済みコンポーネントを使用）
   */
  const renderViewItem = React.useCallback(
    (item: T) => {
      const href = generateViewHref(item);
      return (
        <SidebarSortableItem
          key={item.id}
          item={item}
          href={href}
          pathname={pathname}
          isDragging={isDragging}
          isReorderEnabled={isReorderEnabled}
          className={className}
          renderViewContent={
            renderViewContent as (item: AnySidebarItem) => React.ReactNode
          }
        />
      );
    },
    [
      className,
      generateViewHref,
      renderViewContent,
      isDragging,
      pathname,
      isReorderEnabled,
    ],
  );

  // アイテムをカテゴリごとに分類（メモ化）
  const {
    sortableItems,
    allItem,
    sessionExplorerItem,
    settingsItem,
    otherNonSortableItems,
  } = React.useMemo(() => {
    const { sortable, nonSortable } = categorizeItems(visibleItems);
    const { allItem, sessionExplorerItem, settingsItem, otherItems } =
      extractSpecialItems(nonSortable);

    return {
      sortableItems: sortable,
      allItem,
      sessionExplorerItem,
      settingsItem,
      otherNonSortableItems: otherItems,
    };
  }, [visibleItems]);

  // useIdで自動生成されたIDを使用（サーバー/クライアントで一致が保証される）
  const dndContextId = React.useId();

  // アクティブなアイテムを取得（DragOverlay用）
  const activeItem = React.useMemo(() => {
    return items.find((item) => item.id === activeId);
  }, [activeId, items]);

  return (
    <div ref={containerRef}>
      <DndContext
        id={dndContextId}
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SidebarMenuSub>
          {/* Render "All" item first if it exists */}
          {allItem && renderViewItem(allItem)}

          {/* Render other non-sortable items (if any) */}
          {otherNonSortableItems.map((item: T) => renderViewItem(item))}

          {/* Render sortable items */}
          <SortableContext
            items={sortableItems}
            strategy={verticalListSortingStrategy}
          >
            {sortableItems.map((item: T) => (
              <SortableItemComponent
                key={item.id}
                id={item.id}
                disabled={!isReorderEnabled || isFiltering}
                showDropIndicator={isReorderEnabled && !isFiltering}
                cursorVariant="pointer"
              >
                {renderViewItem(item)}
              </SortableItemComponent>
            ))}
          </SortableContext>

          {/* Render "Session Explorer" item before Settings if it exists */}
          {sessionExplorerItem && renderViewItem(sessionExplorerItem)}

          {/* Render "Settings" item last if it exists */}
          {settingsItem && renderViewItem(settingsItem)}
        </SidebarMenuSub>

        {/* DragOverlay for smooth dragging visualization - ポータル経由 */}
        <DragOverlayPortal activeId={activeId} xOffset={0}>
          {activeItem && (
            <SortableOverlay
              href={generateViewHref(activeItem)}
              isActive={false}
              className={className}
            >
              {renderViewContent(activeItem)}
            </SortableOverlay>
          )}
        </DragOverlayPortal>
      </DndContext>
    </div>
  );
}
